/**
 * The one place that decides whether an attribute name is taken. Callers are
 * written against an async contract, so moving this behind an API is a change to
 * this file alone: gate the commit on `isChecking` (constant `false` today) and
 * render whatever `conflict` says.
 *
 * Two things to keep when you do:
 * 1. Keep the local pass. `localAttributes` includes locals that exist only in the
 *    unsaved draft — for a new space, all of them — which the server cannot see. A
 *    remote check is additive, not a replacement.
 * 2. Fail open on error: report no conflict rather than blocking. The backend is
 *    still the authority on what persists.
 */
import { useMemo } from "react";
import {
  findAttributeNameConflict,
  type AttributeNameConflict,
} from "#common/attributeNameConflicts";
import type { GlobalAttribute, SpaceLocalAttribute } from "#common/types/vault";

export type UseAttributeNameConflictInput = {
  name: string;
  /** Omit when editing an existing local: its slug is frozen and cannot collide. */
  slug?: string;
  globalAttributes: readonly GlobalAttribute[];
  /** Includes locals that exist only in the unsaved draft. */
  localAttributes: readonly SpaceLocalAttribute[];
  ignoreLocalId?: string;
};

export type AttributeNameConflictResult = {
  /** The clash to show, or null — which means "nothing to report", not "verified free". */
  conflict: AttributeNameConflict | null;
  /** True while an answer is outstanding; callers must keep the commit disabled. */
  isChecking: boolean;
};

export function useAttributeNameConflict({
  name,
  slug,
  globalAttributes,
  localAttributes,
  ignoreLocalId,
}: UseAttributeNameConflictInput): AttributeNameConflictResult {
  const conflict = useMemo(
    () =>
      findAttributeNameConflict({
        name,
        slug,
        globalAttributes,
        localAttributes,
        ignoreLocalId,
      }),
    [name, slug, globalAttributes, localAttributes, ignoreLocalId],
  );

  return { conflict, isChecking: false };
}
